import { useState, useRef, useCallback, useEffect } from "react"
import { DndProvider } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"
import "mapbox-gl/dist/mapbox-gl.css"
import { Loader2, PanelLeft } from "lucide-react"
import FacilityMap from "./MapComponent"
import FacilityDrawer from "./Drawer"
import MessageBox from "./MessageBox"
import { Button } from "@/components/ui/button"

const MAPBOX_TOKEN = "pk.eyJ1IjoidGhhYm8tbmRhc2hlIiwiYSI6ImNtYTI1cnZlejE4NnQyaXM4dGg0cmRwbHEifQ.Irw4oNzo0LfpbsyV4JCabg"
const GEOCODING_API_BASE = "https://api.mapbox.com/geocoding/v5/mapbox.places/"
const LOCAL_STORAGE_KEY = "buildingMarkersDataReact"
const LOCAL_STORAGE_VIEW_STATE_KEY = "facilityMapViewState" // Key for storing map center and zoom

export default function Facility() {
  const [markers, setMarkers] = useState([])
  const [viewState, setViewState] = useState(() => {
    // Load initial viewState from localStorage or use default values
    const savedViewState = localStorage.getItem(LOCAL_STORAGE_VIEW_STATE_KEY)
    if (savedViewState) {
      try {
        return JSON.parse(savedViewState)
      } catch {
        console.error("Error parsing saved viewState from localStorage.")
      }
    }
    return {
      longitude: -74.5,
      latitude: 40,
      zoom: 9,
    }
  })
  const [selectedMarkerId, setSelectedMarkerId] = useState(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(true)
  const [message, setMessage] = useState({ text: "", type: "info" })
  const [isLoading, setIsLoading] = useState(true)
  const nextMarkerId = useRef(0)
  const [selectedMarkerType, setSelectedMarkerType] = useState(null)
  const [isInAddMode, setIsInAddMode] = useState(false)

  // --- Message Handling ---
  const showMessage = useCallback((text, type = "info", duration = 3000) => {
    setMessage({ text, type })
    setTimeout(() => {
      setMessage({ text: "", type: "info" })
    }, duration)
  }, [])

  // --- Save Map Center to Local Storage ---
  const handleViewStateChange = useCallback((newViewState) => {
    setViewState(newViewState)
    // Persist the updated viewState to localStorage
    localStorage.setItem(LOCAL_STORAGE_VIEW_STATE_KEY, JSON.stringify(newViewState))
  }, [])

  // --- Load Markers and Adjust Map Center ---
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(LOCAL_STORAGE_KEY)
      if (savedData) {
        const loadedMarkers = JSON.parse(savedData)
        if (Array.isArray(loadedMarkers)) {
          setMarkers(loadedMarkers)

          // Adjust map center to fit most markers if no saved viewState exists
          if (!localStorage.getItem(LOCAL_STORAGE_VIEW_STATE_KEY) && loadedMarkers.length > 0) {
            const longitudes = loadedMarkers.map((m) => m.longitude)
            const latitudes = loadedMarkers.map((m) => m.latitude)
            const avgLongitude = longitudes.reduce((sum, lng) => sum + lng, 0) / longitudes.length
            const avgLatitude = latitudes.reduce((sum, lat) => sum + lat, 0) / latitudes.length

            setViewState((prev) => ({
              ...prev,
              longitude: avgLongitude,
              latitude: avgLatitude,
              zoom: 12, // Adjust zoom level to fit most markers
            }))
          }

          // Update nextMarkerId based on loaded data
          let maxId = -1
          loadedMarkers.forEach((m) => {
            const idNum = Number.parseInt(String(m.id).split("-")[1])
            if (!isNaN(idNum) && idNum > maxId) {
              maxId = idNum
            }
          })
          nextMarkerId.current = maxId + 1
          showMessage(`Loaded ${loadedMarkers.length} markers.`, "success")
        }
      }
    } catch (error) {
      console.error("Error loading markers from localStorage:", error)
      showMessage("Error loading markers.", "error")
      localStorage.removeItem(LOCAL_STORAGE_KEY) // Clear corrupted data
    } finally {
      setIsLoading(false) // Finish loading
    }
  }, [showMessage]) // Run only once on mount

  // --- Save Markers to Local Storage ---
  const handleSaveMarkers = useCallback(() => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(markers))
      showMessage(`Saved ${markers.length} markers successfully.`, "success")
      setIsDrawerOpen(false) // Close drawer after saving
    } catch (error) {
      console.error("Error saving markers to localStorage:", error)
      showMessage("Error saving markers. Storage might be full.", "error")
    }
  }, [markers, showMessage])

  // --- Improved Geocoding ---
  const handleGeocode = useCallback(
    async (address) => {
      if (!address) {
        showMessage("Please enter an address.", "warn")
        return
      }

      // Show loading message
      showMessage("Searching for location...", "info")

      // Add more specific parameters to improve search accuracy
      const params = new URLSearchParams({
        access_token: MAPBOX_TOKEN,
        limit: 5, // Get multiple results to improve chances of finding the right one
        types: "address,poi,place", // Focus on addresses, points of interest, and places
        language: "en", // Set language to English
        fuzzyMatch: "true", // Enable fuzzy matching for better results
      })

      const url = `${GEOCODING_API_BASE}${encodeURIComponent(address)}.json?${params.toString()}`

      try {
        const response = await fetch(url)

        if (!response.ok) {
          throw new Error(`Geocoding API error: ${response.status}`)
        }

        const data = await response.json()

        if (data.features && data.features.length > 0) {
          // Get the first result (most relevant)
          const result = data.features[0]
          const coordinates = result.center
          const placeName = result.place_name

          // Update map view
          setViewState({
            longitude: coordinates[0],
            latitude: coordinates[1],
            zoom: 15,
          })

          // Show success message
          showMessage(`Found: ${placeName}`, "success")
        } else {
          showMessage("Address not found. Try adding more details.", "warn")
        }
      } catch (error) {
        console.error("Geocoding error:", error)
        showMessage("Error finding address. Please try again.", "error")
      }
    },
    [showMessage],
  )

  // --- Marker Management Callbacks ---
  const handleAddMarker = useCallback(
    (markerData) => {
      const newMarker = {
        ...markerData,
        id: `marker-${nextMarkerId.current++}`, // Assign unique ID
      }
      setMarkers((prevMarkers) => [...prevMarkers, newMarker])
      setSelectedMarkerId(newMarker.id) // Select the new marker for editing
      showMessage(`Marker ${newMarker.id} added.`, "info")
    },
    [showMessage],
  )

  const handleUpdateMarker = useCallback((updatedData) => {
    setMarkers((prevMarkers) =>
      prevMarkers.map((marker) => (marker.id === updatedData.id ? { ...marker, ...updatedData } : marker)),
    )
  }, [])

  const handleUpdateMarkerPosition = useCallback((markerId, longitude, latitude) => {
    setMarkers((prevMarkers) =>
      prevMarkers.map((marker) => (marker.id === markerId ? { ...marker, longitude, latitude } : marker)),
    )
  }, [])

  const handleDeleteMarker = useCallback(
    (markerId) => {
      setMarkers((prevMarkers) => prevMarkers.filter((marker) => marker.id !== markerId))
      if (selectedMarkerId === markerId) {
        setSelectedMarkerId(null) // Deselect if the deleted marker was selected
      }
      showMessage(`Marker ${markerId} deleted.`, "info")
    },
    [selectedMarkerId, showMessage],
  )

  const handleSelectMarker = useCallback((markerId) => {
    setSelectedMarkerId(markerId)

    // Ensure drawer is open on mobile when a marker is selected
    if (markerId && window.innerWidth < 768) {
      setIsDrawerOpen(true)
    }
  }, [])

  const handleSelectMarkerType = useCallback((type) => {
    setIsInAddMode(true)
    setSelectedMarkerType(type)
    showMessage(`Selected marker type: ${type.type}`, "info")
  }, [showMessage])

  const handleExitAddMode = useCallback(() => {
    setIsInAddMode(false)
    setSelectedMarkerType(null)
    showMessage("Exited Add Mode.", "info")
  }, [showMessage])

  const handleMapClick = useCallback(
    (event) => {
      if (isInAddMode && selectedMarkerType) {
        const { lng, lat } = event.lngLat
        handleAddMarker({
          longitude: lng,
          latitude: lat,
          type: selectedMarkerType.type,
          color: selectedMarkerType.color,
          title: `New ${selectedMarkerType.type}`,
          description: "",
        })
      } else {
        showMessage("Click on a marker to manage it.", "info")
      }
    },
    [isInAddMode, selectedMarkerType, handleAddMarker, showMessage],
  )

  // --- Drawer Toggle ---
  const handleToggleDrawer = useCallback(() => {
    setIsDrawerOpen((prev) => !prev)
  }, [])

  // --- Handle Drop on Map ---
  const handleDropOnMap = useCallback(
    (item, position) => {
      if (!position) return

      handleAddMarker({
        longitude: position.longitude,
        latitude: position.latitude,
        type: item.type,
        color: item.color,
        title: `New ${item.type}`, // Default title
        description: "",
      })
    },
    [handleAddMarker],
  )

  const selectedMarkerData = markers.find((m) => m.id === selectedMarkerId) || null

  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-lg">Loading map...</span>
      </div>
    )
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="relative h-screen w-screen flex">
        {message.text && <MessageBox message={message.text} type={message.type} />}

        <FacilityMap
          mapboxToken={MAPBOX_TOKEN}
          markers={markers}
          viewState={viewState}
          onViewStateChange={handleViewStateChange} // Use updated handler
          onMarkerClick={handleSelectMarker}
          onMarkerDragEnd={handleUpdateMarkerPosition}
          onMarkerDelete={handleDeleteMarker}
          selectedMarkerId={selectedMarkerId}
          isDrawerOpen={isDrawerOpen}
          onDropOnMap={handleDropOnMap}
          onMapClick={handleMapClick}
        />

        <FacilityDrawer
          isOpen={isDrawerOpen}
          onToggle={handleToggleDrawer}
          onGeocode={handleGeocode}
          onSave={handleSaveMarkers}
          selectedMarker={selectedMarkerData}
          onUpdateMarker={handleUpdateMarker}
          onDeleteMarker={handleDeleteMarker}
          onSelectMarkerType={handleSelectMarkerType}
          onExitAddMode={handleExitAddMode}
        />

        {/* Button to reopen drawer when closed */}
        {!isDrawerOpen && (
          <Button
            variant="outline"
          size="icon"
          className="fixed bottom-4 right-4 shadow-md z-10 bg-background hover:bg-accent"
          onClick={() => setIsDrawerOpen(true)}
          aria-label="Open drawer"
          >
            <PanelLeft className="h-5 w-5" />
          </Button>
        )}
      </div>
    </DndProvider>
  )
}
